import { useEffect, useMemo, useState } from "react";

import { AirshowRepresentationPage } from "./AirshowRepresentationPage";
import { buildAirshowRepresentationRows } from "./airshowRepresentationModel";
import {
  loadBundledLoomAirshowPresetCatalog,
  type LoomAirshowCatalogState,
} from "./loomAirshowPresets";
import { useAirshowHeartbeat } from "./useAirshowHeartbeat";
import { X57_LOCAL_DEMO_PROJECT_ID } from "./x57VspApi";

interface AirshowCoverageSurfaceProps {
  projectId: string;
  branch: string;
  persistedProject: boolean;
  onInspectModel?: (entryId: string) => void;
}

export function AirshowCoverageSurface({
  projectId, branch, persistedProject, onInspectModel,
}: AirshowCoverageSurfaceProps) {
  const [catalogState, setCatalogState] = useState<LoomAirshowCatalogState | null>(null);
  const catalog = catalogState?.kind === "available" ? catalogState.catalog : null;
  const evidenceProjectId = !persistedProject && import.meta.env.DEV
    ? X57_LOCAL_DEMO_PROJECT_ID : projectId;
  const heartbeat = useAirshowHeartbeat({
    active: catalog !== null,
    projectId: evidenceProjectId,
    catalogRevision: catalog?.catalogRevision ?? "unavailable",
    archiveSha256: catalog?.corpus.archiveSha256 ?? "unavailable",
  });

  useEffect(() => {
    let active = true;
    void loadBundledLoomAirshowPresetCatalog().then((state) => {
      if (active) setCatalogState(state);
    });
    return () => { active = false; };
  }, []);

  const labels = useMemo(() => new Map(
    catalog?.entries.map((entry) => [entry.id, entry.label]) ?? [],
  ), [catalog]);
  const rows = useMemo(
    () => buildAirshowRepresentationRows(heartbeat.report?.models ?? [], labels),
    [heartbeat.report?.models, labels],
  );
  const report = heartbeat.report;
  const fullFleet = Boolean(report
    && report.run.scope === "all"
    && report.run.total_models === 353
    && report.run.processed_models === 353
    && report.models.length === 353);

  if (catalogState === null) return <CoverageState title="Loading the verified Airshow catalog…" message="Checking the pinned 353-member archive identity before requesting evidence." />;
  if (catalogState.kind === "unavailable") return <CoverageState error title="Airshow catalog unavailable" message={catalogState.reason} />;
  if (heartbeat.phase === "loading" && !report) return <CoverageState title="Loading the latest Airshow evidence…" message="The page only accepts a terminal, project-scoped heartbeat report." />;
  if (heartbeat.message) return <CoverageState error title="Airshow evidence rejected" message={heartbeat.message} />;
  if (!report) return <CoverageState title="No terminal Airshow report yet" message="Open Fleet heartbeat from Loom Compare and run the pinned corpus. This page will not invent coverage from partial or missing evidence." />;

  return <AirshowRepresentationPage
    comparisonBranch={branch}
    comparisonProjectId={evidenceProjectId}
    fullFleet={fullFleet}
    onInspectModel={onInspectModel}
    reportRevision={report.report_revision}
    rows={rows}
  />;
}

function CoverageState({
  title, message, error = false,
}: { title: string; message: string; error?: boolean }) {
  return <section className="airshow-representations" data-testid="airshow-coverage-state">
    <div className="airshow-representations-hero" role={error ? "alert" : "status"}>
      <div><span>Airshow · representation capability map</span><h2>{title}</h2><p>{message}</p></div>
    </div>
  </section>;
}
